/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref, useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { CurrentKeyDetail, DataState } from '../UNSAFE_Collection';
import { PositionData } from '../UNSAFE_Floating';
import { allTabbableElements } from '../utils/PRIVATE_tabbableUtils';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { isControlOrFunctionKey, KEYS } from '../utils/PRIVATE_keyboardUtils';
import { isSimpleClick } from './eventUtils';

type UseSelectCommonProps<K, D> = {
  data?: DataState<K, D> | null;
  inputRef: Ref<HTMLElement>;
  isFocused?: boolean;
  onFilter?: ({ searchText }: { searchText?: string }) => void;
};

/**
 * Hook that manages state and behavior for a Select* component.  This hook creates state
 * variables and event listeners, returning properties to apply to components internally
 * rendered by a Select* component, as well as state information.
 *
 * @param data Specifies data for the dropdown list.
 * @param inputRef Ref to the input element.
 * @param isFocused Specifies whether the component has focus.
 * @param onFilter Callback function to trigger loading data for the dropdown list,
 * which may or may not be filtered by user entered text.
 *
 * @returns Component state and event listeners.
 */
export function useSelectCommon<K, D>({
  data,
  inputRef,
  isFocused,
  onFilter
}: UseSelectCommonProps<K, D>) {
  const mainFieldRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isDropdownAbove, setDropdownAbove] = useState(false);
  const [currentRow, setCurrentRow] = useState<{ rowKey?: K }>({
    rowKey: undefined
  });
  const [isUserFiltering, setUserFiltering] = useState(false);
  const [userInput, setUserInput] = useState<string>();
  const [searchText, setSearchText] = useState<string>();

  const [prevIsDropdownOpen, setPrevIsDropdownOpen] = useState(isDropdownOpen);
  const [prevIsFocused, setPrevIsFocused] = useState(isFocused);
  const [prevIsUserFiltering, setPrevIsUserFiltering] = useState(isUserFiltering);

  const onMouseDown = useCallback(
    (event: MouseEvent) => {
      // const target = event.target as HTMLElement;
      if (event.defaultPrevented || !isSimpleClick(event)) {
        return;
      }
      if (!isDropdownOpen) {
        setDropdownOpen(true);
      }
      // this is needed to focus the input when clicking on the inside label when there is no
      // value;  otherwise the component looks focused but doesn't actually have physical focus
      setTimeout(function () {
        inputRef.current?.focus();
      }, 0);
    },
    [inputRef, isDropdownOpen]
  );

  const handleDropdownArrowClick = useCallback(() => {
    setDropdownOpen(!isDropdownOpen);

    // focus the input so that if the user Tabs afterwards, it loses focus and the dropdown closes
    inputRef.current?.focus();
  }, [inputRef, isDropdownOpen]);

  const handleDropdownAutoDismiss = useCallback(
    (event?: Event) => {
      if (isDropdownOpen) {
        setDropdownOpen(false);

        switch (event?.type) {
          case 'keydown':
            const keyboardEvent = event as KeyboardEvent;
            switch (keyboardEvent.code) {
              case KEYS.ESC:
                // focus the input when the user cancels out of the dropdown
                inputRef.current?.focus();
                break;
              default:
                break;
            }
            break;
          case 'mousedown':
            // if focus is in the dropdown, focus the input before the dropdown is hidden so that
            // focus will transfer correctly when the target is not focusable;
            // otherwise the text cursor appears in the input again
            if (dropdownRef.current?.contains(document.activeElement)) {
              inputRef.current?.focus();
            }
            break;
          default:
            break;
        }
      }
    },
    [inputRef, isDropdownOpen]
  );

  const handleDropdownPosition = useCallback((data: PositionData) => {
    setDropdownAbove(data.placement.startsWith('top'));
  }, []);

  const getDropdownTabbableElems = useCallback(() => {
    return isDropdownOpen && dropdownRef.current
      ? allTabbableElements(dropdownRef.current)
      : undefined;
  }, [isDropdownOpen]);

  const handleUpDownArrowKeys = useCallback(
    (event: KeyboardEvent) => {
      if (!isDropdownOpen) {
        setDropdownOpen(true);
      } else {
        // transfer physical focus into the dropdown
        const tabbableElems = getDropdownTabbableElems();
        tabbableElems?.[0]?.focus();

        // if there is no current item set, make the first item current
        if (data !== undefined && currentRow.rowKey === undefined) {
          setCurrentRow({ rowKey: data?.data?.[0]?.metadata?.key });
        }
      }

      // prevent the page from scrolling
      event.preventDefault();
    },
    [currentRow, data, getDropdownTabbableElems, isDropdownOpen]
  );

  const handleMainFieldKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // const target = event.target as HTMLElement;
      // ignore control key and function key
      if (isControlOrFunctionKey(event)) {
        return;
      }

      switch (event.code) {
        case KEYS.PAGE_UP:
        case KEYS.PAGE_DOWN:
          // prevent the page from scrolling
          event.preventDefault();

          return;

        case KEYS.UP:
        case KEYS.DOWN:
          // prevent the page from scrolling
          event.preventDefault();
          break;

        case KEYS.ESC:
          if (isDropdownOpen) {
            setDropdownOpen(false);

            event.preventDefault();

            // JET-53183 - core pack compat: stop propagation of handled events
            // we don't want a parent collection component to stop editing the row when we close
            // the dropdown
            event.stopPropagation();
          }
          break;

        default:
          break;
      }
    },
    [isDropdownOpen]
  );

  const handleMainFieldKeyUp = useCallback(
    (event: KeyboardEvent) => {
      // ignore control key and function key
      if (isControlOrFunctionKey(event)) {
        return;
      }

      switch (event.code) {
        case KEYS.UP:
        case KEYS.DOWN:
          handleUpDownArrowKeys(event);
          break;

        default:
          break;
      }
    },
    [handleUpDownArrowKeys]
  );

  const handleInput = useCallback(
    (detail: ValueUpdateDetail<string>) => {
      if (!isDropdownOpen) {
        setDropdownOpen(true);
      }
      setUserFiltering(true);
      setUserInput(detail.value);
      const str = detail.value;
      setSearchText(str);
      onFilter?.({ searchText: str });
    },
    [isDropdownOpen, onFilter]
  );

  const onCurrentKeyChange = useCallback((detail: CurrentKeyDetail<K>) => {
    setCurrentRow({ rowKey: detail.value });
  }, []);

  const stopFiltering = useCallback(() => {
    setUserFiltering(false);
    setUserInput('');
    setSearchText('');
  }, []);

  // the user is not actively filtering if the dropdown is closed and the component doesn't
  // have focus
  if (
    prevIsDropdownOpen !== isDropdownOpen ||
    prevIsFocused !== isFocused ||
    prevIsUserFiltering !== isUserFiltering
  ) {
    if (!isDropdownOpen && !isFocused && isUserFiltering) {
      stopFiltering();
    }
  }

  // need to call onFilter with undefined searchText when the dropdown opens and the
  // user is NOT filtering
  useEffect(() => {
    if (isDropdownOpen && !isUserFiltering) {
      onFilter?.({ searchText: undefined });
    }
  }, [isDropdownOpen, isUserFiltering, onFilter]);

  // when the dropdown closes, reset the isDropdownAbove flag
  if (prevIsDropdownOpen !== isDropdownOpen) {
    if (!isDropdownOpen) {
      setDropdownAbove(false);
    }
    setPrevIsDropdownOpen(isDropdownOpen);
  }

  if (prevIsFocused !== isFocused) {
    setPrevIsFocused(isFocused);
  }
  if (prevIsUserFiltering !== isUserFiltering) {
    setPrevIsUserFiltering(isUserFiltering);
  }

  return {
    currentRow,
    dropdownRef,
    getDropdownTabbableElems,
    handleDropdownArrowClick,
    handleDropdownAutoDismiss,
    handleDropdownPosition,
    handleInput,
    handleMainFieldKeyDown,
    handleMainFieldKeyUp,
    handleUpDownArrowKeys,
    isDropdownAbove,
    isDropdownOpen,
    // the field should remain to look focused while the dropdown is open,
    // in case physical focus is in the dropdown
    isFocused: isFocused || isDropdownOpen,
    isUserFiltering,
    mainFieldRef,
    onCurrentKeyChange,
    onMouseDown,
    searchText,
    setCurrentRow,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    userInput
  };
}
